import React, { useEffect, useMemo, useRef, useState, forwardRef, useImperativeHandle } from "react";
import { ScrollView, View, StyleSheet } from "react-native";
import ThemedInput from "@/components/ThemedInput";
import { InvestigationForm, InvestigationPayload } from "./types";
import { theme } from "@/constants/themes";
import { UserInput } from "@/components/inputs/UserInput";
import { IncidentTypeInput } from "@/components/inputs/IncidentTypeInput";
import { IncidentsInput } from "@/components/inputs/IncidentsInput";

export { InvestigationPayload } from "./types";
export type { InvestigationForm } from "./types";

// Status options for dropdown
const STATUS_OPTIONS = [
  { text: "PENDING", color: "pending" },
  { text: "IN PROGRESS", color: "todo" },
  { text: "COMPLETED", color: "complete" },
] as const;

export function getInvestigationStatusColor(status: string, colors: theme): string {
  const mapping: Record<string, string> = {
    PENDING: colors.pending.border,
    "IN PROGRESS": colors.todo.border,
    COMPLETED: colors.complete.border,
  };
  return mapping[status.toUpperCase()] || colors.neutral.border;
}

export const INVESTIGATION_STATUS_OPTIONS = STATUS_OPTIONS;

export interface InvestigationCreateFormProps {
  initialInvestigation?: Partial<InvestigationForm>;
  onSave?: (payload: InvestigationPayload) => Promise<void> | void;
  onChange?: (payload: InvestigationPayload) => void;
  onDirtyChange?: (dirty: boolean) => void;
}

export interface InvestigationCreateFormRef {
  handleSave: () => Promise<void>;
  handleReset: () => void;
  updateInvestigationName: (name: string) => void;
  updateInvestigationStatus: (status: string) => void;
}

export const InvestigationCreateForm = forwardRef<InvestigationCreateFormRef, InvestigationCreateFormProps>(
  ({ initialInvestigation, onSave, onChange, onDirtyChange }, ref) => {
    // ---------- form state ----------
    const [investigation, setInvestigation] = useState<InvestigationForm>({
      name: initialInvestigation?.name ?? "",
      description: initialInvestigation?.description ?? "",
      details: initialInvestigation?.details ?? "",
      status: initialInvestigation?.status ?? "PENDING",
      lead_investigator_id: initialInvestigation?.lead_investigator_id ?? null,
      incident_type_id: initialInvestigation?.incident_type_id ?? null,
      incidents: initialInvestigation?.incidents ?? [],
      findings: initialInvestigation?.findings ?? "",
      recommendations: initialInvestigation?.recommendations ?? "",
    });

    useEffect(() => {
      onChange?.({ investigation });
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [investigation]);

    const baseline = useRef(JSON.stringify({ investigation }));
    const dirty = useMemo(() => JSON.stringify({ investigation }) !== baseline.current, [investigation]);

    // Notify parent of dirty state changes
    useEffect(() => {
      onDirtyChange?.(dirty);
    }, [dirty, onDirtyChange]);

    // Expose methods to parent via ref
    useImperativeHandle(ref, () => ({
      handleSave,
      handleReset,
      updateInvestigationName: (name: string) => {
        setInvestigation((i) => ({ ...i, name }));
      },
      updateInvestigationStatus: (status: string) => {
        setInvestigation((i) => ({ ...i, status }));
      },
    }));

    // ---------- helpers ----------
    const updateInvestigation = (patch: Partial<InvestigationForm>) => setInvestigation((i) => ({ ...i, ...patch }));

    const handleSave = async () => {
      try {
        await onSave?.({ investigation });
        baseline.current = JSON.stringify({ investigation });
      } catch (e: any) {
        throw e;
      }
    };

    const handleReset = () => {
      const nextInvestigation: InvestigationForm = {
        name: initialInvestigation?.name ?? "",
        description: initialInvestigation?.description ?? "",
        details: initialInvestigation?.details ?? "",
        status: initialInvestigation?.status ?? "PENDING",
        lead_investigator_id: initialInvestigation?.lead_investigator_id ?? null,
        incident_type_id: initialInvestigation?.incident_type_id ?? null,
        incidents: initialInvestigation?.incidents ?? [],
        findings: initialInvestigation?.findings ?? "",
        recommendations: initialInvestigation?.recommendations ?? "",
      };
      setInvestigation(nextInvestigation);
      baseline.current = JSON.stringify({ investigation: nextInvestigation });
    };

    return (
      <View style={styles.container}>
        <ScrollView contentContainerStyle={{ gap: 16 }}>
          {/* Lead Investigator */}
          <UserInput
            value={investigation.lead_investigator_id}
            state="editing"
            label="Lead Investigator"
            placeholder="Select lead investigator (optional)"
            onChange={(userId) => updateInvestigation({ lead_investigator_id: userId })}
          />

          {/* Incident Type */}
          <IncidentTypeInput
            value={investigation.incident_type_id}
            state="editing"
            label="Incident Type"
            placeholder="Select incident type (optional)"
            onChange={(typeId) => updateInvestigation({ incident_type_id: typeId })}
          />

          {/* Linked Incidents */}
          <IncidentsInput
            value={investigation.incidents}
            state="editing"
            label="Linked Incidents"
            onChange={(incidents) => updateInvestigation({ incidents })}
          />

          <ThemedInput
            testID="investigation-description-input"
            label="Description"
            placeholder="Describe the investigation..."
            value={investigation.description}
            onChangeText={(text) => updateInvestigation({ description: text })}
            multiline
            inputStyle={{ minHeight: 80, textAlignVertical: "top" }}
            labelStyle={styles.label}
          />

          <ThemedInput
            testID="investigation-details-input"
            label="Details"
            placeholder="Additional details..."
            value={investigation.details}
            onChangeText={(text) => updateInvestigation({ details: text })}
            multiline
            inputStyle={{ minHeight: 120, textAlignVertical: "top" }}
            labelStyle={styles.label}
          />

          <ThemedInput
            testID="investigation-findings-input"
            label="Findings"
            placeholder="Document investigation findings..."
            value={investigation.findings}
            onChangeText={(text) => updateInvestigation({ findings: text })}
            multiline
            inputStyle={{ minHeight: 100, textAlignVertical: "top" }}
            labelStyle={styles.label}
          />

          <ThemedInput
            testID="investigation-recommendations-input"
            label="Recommendations"
            placeholder="Document recommendations..."
            value={investigation.recommendations}
            onChangeText={(text) => updateInvestigation({ recommendations: text })}
            multiline
            inputStyle={{ minHeight: 100, textAlignVertical: "top" }}
            labelStyle={styles.label}
          />
        </ScrollView>
      </View>
    );
  },
);

InvestigationCreateForm.displayName = "InvestigationCreateForm";

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  label: {
    fontSize: 11,
    fontWeight: "600",
    textTransform: "uppercase",
    letterSpacing: 0.5,
  },
});
